import { AcsOfflineI18nConfigurationProvider } from '@amzn/i18n.acsoffline-i18n-configuration-provider-typescript';
import {
    ConfigStage,
    ConfigurationProviderManager,
    CXTupleToDefaultPreferences,
    CXTupleToDefaultPreferencesArgs,
    CXTupleToI18nDomain,
    CXTupleToI18nDomainArgs,
    I18nConfigurationProvider,
    MarketplaceIdToCxTuple,
    MarketplaceIdToCxTupleArgs
} from '@amzn/i18n.configuration-provider';
import { CXTuple } from './interfaces';
import { MARKETPLACE_DEFAULTS } from './marketplace-defaults';

export class MarketplaceHelper {
    private static MARKETPLACE_STAGE_MAP: Map<string, ConfigStage> = MarketplaceHelper.getMarketplaceStages();
    private static I18N_CONFIG_PROVIDER = MarketplaceHelper.registerProvider();

    /**
     * Fetch the cxTuple based on the marketplaceId and config stage.
     * @param obfuscatedMarketplaceId the target marketplace ID.
     */
    public static fetchCxTuple(obfuscatedMarketplaceId: string | undefined): CXTuple | undefined {
        if (!obfuscatedMarketplaceId) {
            return undefined;
        }

        const configStage = MarketplaceHelper.fetchMarketplaceStage(obfuscatedMarketplaceId);
        const args = new MarketplaceIdToCxTupleArgs.Builder()
            .withObfuscatedMarketplaceId(obfuscatedMarketplaceId)
            .build();

        const cxFamily = MarketplaceHelper.I18N_CONFIG_PROVIDER?.getConfigurationWithStage(
            MarketplaceIdToCxTuple.CX_FAMILY,
            args,
            configStage
        );

        const cxId = MarketplaceHelper.I18N_CONFIG_PROVIDER?.getConfigurationWithStage(
            MarketplaceIdToCxTuple.CX_ID,
            args,
            configStage
        );

        if (!cxFamily?.getStringValue() || !cxId?.getStringValue()) {
            return undefined;
        }

        return {
            cxFamily: cxFamily?.getStringValue()!!,
            cxId: cxId?.getStringValue()!!
        };
    }

    /**
     * Fetch the default locales based on the cxTuple.
     * @param cxTuple the target cxTuple.
     */
    public static fetchDefaultLocale(cxTuple: CXTuple | undefined): string | undefined {
        return this.fetchDefault(cxTuple, CXTupleToDefaultPreferences.DEFAULT_LANGUAGE);
    }

    /**
     * Fetch the default currency based on the cxTuple.
     * @param cxTuple the target cxTuple.
     */
    public static fetchDefaultCurrency(cxTuple: CXTuple | undefined): string | undefined {
        return this.fetchDefault(cxTuple, CXTupleToDefaultPreferences.DEFAULT_CURRENCY);
    }

    /**
     * Fetch the i18nDomain based on cxTuple
     * @param cxTuple the target i18nDomain
     */
    public static fetchI18nDomain(cxTuple: CXTuple | undefined): string | undefined {
        if (!cxTuple) {
            return undefined;
        }

        const args = new CXTupleToI18nDomainArgs.Builder()
            .withCxFamily(cxTuple.cxFamily)
            .withCxId(cxTuple.cxId)
            .build();

        const i18nDomain = MarketplaceHelper.I18N_CONFIG_PROVIDER!!.getConfiguration(
            CXTupleToI18nDomain.I18N_DOMAIN,
            args
        );

        return i18nDomain.getStringValue() || undefined;
    }

    private static fetchMarketplaceStage(obfuscatedMarketplaceId: string | undefined): ConfigStage {
        if (!obfuscatedMarketplaceId) {
            return ConfigStage.PROD;
        }

        return MarketplaceHelper.MARKETPLACE_STAGE_MAP.get(obfuscatedMarketplaceId) || ConfigStage.PROD;
    }

    private static registerProvider(): I18nConfigurationProvider {
        // Register the AcsOfflineI18nConfigurationProvider if not registered.
        if (!ConfigurationProviderManager.hasProvider()) {
            ConfigurationProviderManager.register(new AcsOfflineI18nConfigurationProvider());
        }

        return ConfigurationProviderManager.getProvider()!!;
    }

    private static fetchDefault(cxTuple: CXTuple | undefined, target: CXTupleToDefaultPreferences): string | undefined {
        if (!cxTuple) {
            return undefined;
        }

        const args = new CXTupleToDefaultPreferencesArgs.Builder()
            .withCxFamily(cxTuple.cxFamily)
            .withCxId(cxTuple.cxId)
            .build();

        const defaultData = MarketplaceHelper.I18N_CONFIG_PROVIDER!!.getConfiguration(target, args);
        return defaultData.getStringValue() || undefined;
    }

    private static getMarketplaceStages(): Map<string, ConfigStage> {
        const marketplaceStages = new Map<string, ConfigStage>();
        for (const marketplaceId of Object.keys(MARKETPLACE_DEFAULTS)) {
            const configStage = MARKETPLACE_DEFAULTS[marketplaceId].name.endsWith('DEVO')
                ? ConfigStage.DEVO
                : ConfigStage.PROD;
            marketplaceStages.set(marketplaceId, configStage);
        }

        return marketplaceStages;
    }
}
